import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  CircularProgress,
  CardHeader,
  InputLabel,
  Select,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import { getInvoicesByClientId, getInvoicesByContractId } from "../../API/billingAPI";

function InvoicesTable({
  source,
  contractSource,
  selectedClient,
  selectedContract,
  invoices,
  loading: externalLoading,
  onInvoicesFetch,
  hasSearched,
  hideFilters = false,
  hideCreateButton = false,
  readOnly = false,
}) {
  const CURRENCY = 'PHP';
  const navigate = useNavigate();
  const isExternalMode = invoices !== undefined;
  const [ internalInvoices, setInternalInvoices ] = useState([]);
  const [ internalLoading, setInternalLoading ] = useState(false);
  const [ menuAnchor, setMenuAnchor ] = useState(null);
  const [ selectedInvoiceForMenu, setSelectedInvoiceForMenu ] = useState(null);
  const [ searchLoading, setSearchLoading ] = useState(false);
  const [ filters, setFilters ] = useState({
    status: '',
    billingDateOrder: 'desc',
  });
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const displayedInvoices = isExternalMode? invoices : internalInvoices;
  const loading = isExternalMode ? externalLoading: internalLoading;
  const sortedInvoices = useMemo(() => {
    return [...displayedInvoices].sort((a, b) => {
      const dateA = a.billing_date || '';
      const dateB = b.billing_date || '';
      const comparison = dateA.localeCompare(dateB);
      return filters.billingDateOrder === 'asc' ? comparison : -comparison;
    });
  }, [displayedInvoices, filters.billingDateOrder]);
  const totalRows = sortedInvoices.length;
  const paginatedInvoices = sortedInvoices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const revenueSummary = useMemo(() => {
    const projectedRevenue = selectedContract?.price_minor != null
      ? Number(selectedContract.price_minor) || 0
      : sortedInvoices.reduce(
          (sum, invoice) => sum + (Number(invoice.total_due_minor) || 0),
          0
        );
    const actualRevenue = sortedInvoices
      .filter((invoice) => invoice.status === 'PAID')
      .reduce((sum, invoice) => sum + (Number(invoice.total_due_minor) || 0), 0);

    return {
      projectedRevenue,
      actualRevenue,
      remaining: projectedRevenue - actualRevenue,
    };
  }, [selectedContract?.price_minor, sortedInvoices]);
  const filtersDisabled = loading || searchLoading;
  const formatMoney = (minor) =>
    `${CURRENCY} ${(Number(minor) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  useEffect(() => {
    if (isExternalMode || !selectedClient?.id || !hasSearched) return;

    let active = true;
    setInternalLoading(true);
    getInvoicesByClientId(selectedClient.id)
      .then((data) => {
        if (!active) return;
        setInternalInvoices(data?.items ?? []);
      })
      .catch(() => { if (active) setInternalInvoices([]); })
      .finally(() => { if (active) setInternalLoading(false); });
      return () => { active = false; };
  }, [selectedClient?.id, hasSearched, isExternalMode]);

  useEffect(() => {
    setPage(0);
  }, [selectedClient?.id, selectedContract?.contract_id, displayedInvoices.length]);

  useEffect(() => {
    handleClearFilters();
  }, [selectedClient?.id]);

  const handleClearFilters = async () => {
    const resetFilters = {
      status: '',
      billingDateOrder: 'desc',
    };

    setFilters(resetFilters);
    setPage(0);

    if (!selectedClient?.id && !selectedContract?.contract_id) return;

    try {
      setSearchLoading(true);

      let result;

      if (selectedContract?.contract_id) {
        result = await getInvoicesByContractId(selectedContract.contract_id);
      } else {
        result = await getInvoicesByClientId(selectedClient.id);
      }

      const fetched = result?.items ?? result?.invoices ?? result ?? [];

      if (!isExternalMode) {
        setInternalInvoices(Array.isArray(fetched) ? fetched : []);
      } else {
        onInvoicesFetch?.(Array.isArray(fetched) ? fetched : []);
      }
    } catch (error) {
      console.error('Error clearing invoice filters:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleMenuOpen = (event, invoice) => {
    setMenuAnchor(event.currentTarget);
    setSelectedInvoiceForMenu(invoice);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedInvoiceForMenu(null);
  };

  const handleDeleteClick = () => {
    navigate('/delete-invoice', {
      state: {
        invoice: selectedInvoiceForMenu,
        selectedClient,
        selectedContract,
        source: source || 'manageContracts',
        contractSource,
      },
    });
    setMenuAnchor(null);
  };

  const handleEditClick = (invoice) => {
    navigate('/edit-invoice', {
      state: {
        invoice,
        selectedClient,
        selectedContract,
        source: source || 'manageContracts',
        contractSource,
      },
    });
    handleMenuClose();
  };

  const handleViewClick = (invoice) => {
    navigate('/view-invoice', {
      state: {
        invoice,
        selectedClient,
        selectedContract,
        source: source || 'manageContracts',
        contractSource,
      },
    });
    handleMenuClose();
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(0);
  };

  const handleSearch = async () => {
    try {
      setSearchLoading(true);

      const activeFilters = {}
      if (filters.status) activeFilters.status = filters.status;

      let result;
      if (selectedContract?.contract_id) {
        result = await getInvoicesByContractId(selectedContract.contract_id, activeFilters);
      } else if (selectedClient) {
        result = await getInvoicesByClientId(selectedClient.id, activeFilters);
      } 

      if (result) {
        const fetched = result.items ?? result.invoices ?? result;
        if (!isExternalMode) {
          setInternalInvoices(Array.isArray(fetched) ? fetched : []);
        } else {
          onInvoicesFetch?.(fetched);
        }
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID':
        return '#155724';
      case 'OVERDUE':
        return '#721c24';
      case 'OPEN':
        return '#978d2d';
      default:
        return '#f5f5f5';
    }
  };

  const getStatusBgColor = (status) => {
    switch(status) {
      case 'PAID':
        return '#d4edda';
      case 'OVERDUE':
        return '#f8d7da';
      case 'OPEN':
        return '#fff9c4';
      default:
        return '#f5f5f5';
    };
  };

  if (loading && displayedInvoices.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={30} sx={{ mr: 2 }} />
        <Typography>Loading invoices...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CardHeader
          title={
            <Typography
              variant='h6'
              sx={{
                paddingBottom: '0.5rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                fontSize: '1rem',
              }}
            >
              Invoices
            </Typography>
          }
          sx={{ pb: 0, pt: 0, pl: 0 }}
        />
      </Box>
      {/* Search Filters */}
      {!hideFilters && (
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems='flex-end'>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size='small' disabled={filtersDisabled}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label='Status'
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='OPEN'>Open</MenuItem>
                <MenuItem value='PAID'>Paid</MenuItem>
                <MenuItem value='OVERDUE'>Overdue</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size='small' disabled={filtersDisabled}>
              <InputLabel>Billing Date Order</InputLabel>
              <Select
                value={filters.billingDateOrder}
                onChange={(e) => handleFilterChange('billingDateOrder', e.target.value)}
                label='Billing Date Order'
              >
                <MenuItem value='desc'>Newest first</MenuItem>
                <MenuItem value='asc'>Oldest first</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 2}}>
              <Button
                variant='contained'
                onClick={handleClearFilters}
                disabled={filtersDisabled}
              >
                Clear
              </Button>
              <Button
                variant='contained'
                onClick={handleSearch}
                disabled={filtersDisabled}
              >
                {searchLoading ? <CircularProgress size={20} /> : 'Search'}
              </Button>
              {!hideCreateButton && (
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => navigate('/create-invoice', {
                    state: {
                      selectedClient,
                      selectedContract,
                      source: source || "manageContracts",
                    }
                  })}
                >
                  Create Invoice
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={30} sx={{ mr: 2 }} />
          <Typography>Loading invoices...</Typography>
        </Box>
      ): displayedInvoices.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 3 }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>Invoice Number</TableCell>
                  <TableCell>Billing Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Total Due</TableCell>
                  <TableCell>Status</TableCell>
                  {!readOnly && (
                    <TableCell align="center">Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInvoices.map((invoice) => (
                  <TableRow key={invoice.invoice_id}>
                    <TableCell>{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.billing_date}</TableCell>
                    <TableCell>{invoice.due_date}</TableCell>
                    <TableCell>{formatMoney(invoice.total_due_minor)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '1',
                          backgroundColor: getStatusBgColor(invoice.status),
                          color: getStatusColor(invoice.status),
                          padding: '4px 8px',
                          fontWeight: 'bold',
                        }}
                      >
                        {invoice.status}
                      </Typography>
                    </TableCell>
                    {!readOnly && (
                      <TableCell align="center">
                        <Stack direction='row' spacing={1} justifyContent='center'>
                          <Button
                            variant='outlined'
                            size='small'
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewClick(invoice)}
                          >
                            View
                          </Button>
                          <Button
                            variant='outlined'
                            size='small'
                            startIcon={<EditIcon />}
                            onClick={() => handleEditClick(invoice)}
                          >
                            Edit
                          </Button>
                          <IconButton
                            size='small'
                            onClick={(e) => handleMenuOpen(e, invoice)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                px: 2,
                py: 1.5,
                borderTop: "1px solid #eee",
                backgroundColor: "#fafafa",
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Projected Revenue
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {formatMoney(revenueSummary.projectedRevenue)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Actual Revenue (Paid)
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#155724" }}>
                  {formatMoney(revenueSummary.actualRevenue)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Remaining
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "bold",
                    color: revenueSummary.remaining > 0 ? "#721c24" : "#155724",
                  }}
                >
                  {formatMoney(revenueSummary.remaining)}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                borderTop: "1px solid #eee",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Results: {totalRows}
              </Typography>

              <TablePagination
                component="div"
                count={totalRows}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10]}
              />
            </Box>
          </TableContainer>
      )}
      
      {/* Menu */}
      {!readOnly && (
        <>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleDeleteClick}>
              <DeleteIcon sx={{ mr: 1 }} fontSize='small' />
              Delete
            </MenuItem>
          </Menu>

        </>
      )}

      {/* No invoices Message */}
      {!loading && (isExternalMode || hasSearched) && displayedInvoices.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color='text.secondary' variant='body2'>
            No invoices found for this client
          </Typography>
        </Box>
      )}
    </>
  );
}

export default InvoicesTable;
